import { Loading, Module, ModuleBody, ModuleHeader } from '@wfp/react';
import { Infographics } from 'acr_ui/sources/components/Infographics';
import { WFPContributionToSDGs } from 'acr_ui/sources/components/WFPContributionToSDGs';
import orderBy from 'lodash/orderBy';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useDispatch, useSelector } from 'react-redux';

import ImageUploader from 'src/components/ImageUploader';
import BottomLinks from 'src/components/ReportTemplate/Workspace/BottomLinks';
import { DATA_KEY_ON_DATA_SET } from 'src/components/ReportTemplate/Workspace/config';
import ContactInfo from 'src/components/ReportTemplate/Workspace/ContactInfo';
import DataNotes from 'src/components/ReportTemplate/Workspace/DataNotes';
import LatestRefreshInfo from 'src/components/ReportTemplate/Workspace/LatestRefreshInfo';
import Title from 'src/components/ReportTemplate/Workspace/Title';
import WordLimitPanel from 'src/components/ReportTemplate/Workspace/WordLimitPanel';
import Narrative from 'src/components/ReportTemplate/WorkspaceACR2023/Narrative';
import SectionControls from 'src/components/ReportTemplate/WorkspaceACR2023/SectionControls';
import Subtitle from 'src/components/ReportTemplate/WorkspaceACR2023/Subtitle';
import { useRefreshDataSource } from 'src/hooks';
import {
  requestAPI,
  requestAPIHoldingData,
  requestAPILatest,
} from 'src/redux/api/actions';
import {
  GET_APP_CONFIGURATION,
  ICONS_DETAILS_KEY,
  REPORT_DETAIL_DATA_KEY,
  SECTION_DETAIL_DATA_KEY,
  SECTION_REFRESH_DATA_SOURCE_KEY,
  WORDCOUNT_CONFIG,
} from 'src/redux/constants';
import { getIconsDetails } from 'src/redux/icons/actions';
import {
  LockInfographicsAction,
  UnlockInfographicsAction,
  UpdateInfographicsAction,
} from 'src/redux/infographics/actions';
import type { RootState } from 'src/redux/store';
import type { IDataSource } from 'src/types';
import { getReportPageTitle, getShowWordcount, sendGAEvent } from 'src/utils';
import {
  DATA_SOURCES_WIDGETS_MAP,
  getDataSourceWidgetsParametersMap,
} from './dataSourceWidgetsConfig';

interface ILastRefreshInfo {
  date: string;
  type: string;
  user: string;
}

type ILastRefreshInfoList = ILastRefreshInfo[];

const DATA_SOURCES_REMOVED_FROM_2026 = [
  'annual_food_transfer',
  'annual_cash_based_transfer_and_commodity_voucher',
];

/** Data source ids the workspace treats specially. */
const TOTAL_BENEFICIARIES_ID = 'total_beneficiaries';
const SDG_CONTRIBUTION_ID = 'wfp_contribution_to_sdgs';
const FINANCIAL_OVERVIEW_ID =
  'annual_financial_overview_by_strategic_outcome_and_activity';
const HEADING_WIDGET_IDS = ['strategic_outcome_title'];

interface IData {
  id: string;
  title: string;
  lastRefreshInfo?: ILastRefreshInfoList[];
  containerClassName?: string;
  noModuleTitle?: any;
  component?: React.JSX.Element;
}

const WorkspaceACR2023 = () => {
  const dispatch = useDispatch();

  const reportDetailData = useSelector(
    (state: RootState) => state.viewData[REPORT_DETAIL_DATA_KEY].data,
  );
  const sectionDetailData = useSelector(
    (state: RootState) => state.viewData?.[SECTION_DETAIL_DATA_KEY]?.data,
  );
  const sectionDetailError = useSelector(
    (state: RootState) => state.viewData?.[SECTION_DETAIL_DATA_KEY]?.error,
  );
  const viewState = useSelector((state: RootState) => state.viewState);
  const iconsDetailsData = useSelector(
    (state: RootState) => state.viewData?.[ICONS_DETAILS_KEY]?.data,
  );
  const wordcountConfig = useSelector(
    (state: RootState) => state.viewData?.[WORDCOUNT_CONFIG]?.data,
  );
  const dataSourceRefresh = useSelector(
    (state: RootState) => state.viewData?.[SECTION_REFRESH_DATA_SOURCE_KEY],
  );
  const userData = useSelector(
    (state: RootState) => state.viewData?.[GET_APP_CONFIGURATION]?.data,
  );
  const currentUserUsername = userData?.userInfo?.username;

  const [isEditInfographics, setIsEditInfographics] = useState(false);

  const [handleRefreshDataSources] = useRefreshDataSource();

  const isStrategicOutcome = sectionDetailData?.current_title
    .toLowerCase()
    .includes('strategic outcome');

  const headingWidgets = [],
    remainingWidgets = [];

  const createdDataSource = useMemo(() => {
    if (sectionDetailData?.data_sources) {
      const dataSources =
        parseInt(reportDetailData?.period, 10) >= 2026
          ? sectionDetailData.data_sources.filter(
              (dataSource: IDataSource) =>
                !DATA_SOURCES_REMOVED_FROM_2026.includes(dataSource.id),
            )
          : sectionDetailData.data_sources;
      return dataSources.map(getComponentForDataSource);
    }

    return [];
  }, [sectionDetailData, reportDetailData?.period]);

  for (let i = 0, len = createdDataSource.length; i < len; i++) {
    const dataSourceObject = createdDataSource[i];
    if (HEADING_WIDGET_IDS.includes(dataSourceObject?.id)) {
      headingWidgets.push(dataSourceObject);
    } else {
      remainingWidgets.push(dataSourceObject);
    }
  }

  const showWordcount = getShowWordcount(
    sectionDetailData?.current_title,
    reportDetailData?.permissions?.has_permission_to_view_word_count,
  );

  useEffect(() => {
    dispatch(getIconsDetails());
  }, []);

  useEffect(() => {
    if (
      sectionDetailData?.infographics_editor?.infographics_editor &&
      sectionDetailData.infographics_editor.infographics_editor ===
        currentUserUsername
    ) {
      setIsEditInfographics(true);
    }
  }, [sectionDetailData?.infographics_editor, currentUserUsername]);

  useEffect(() => {
    if (viewState?.activeSectionID) {
      dispatch(
        requestAPILatest(SECTION_DETAIL_DATA_KEY, {
          url_format: { _id_: viewState.activeSectionID },
        }),
      );

      setIsEditInfographics(false);
    }
  }, [viewState?.activeSectionID]);

  const fetchWordcountIfAllowedRef = useRef<() => void>(() => undefined);
  fetchWordcountIfAllowedRef.current = () => {
    if (viewState?.activeSectionID && showWordcount) {
      getReportWordcountConfig();
    }
  };

  useEffect(() => {
    fetchWordcountIfAllowedRef.current();
  }, [sectionDetailData?.current_title]);

  useEffect(() => {
    if (!dataSourceRefresh?.error && reportDetailData.reportPk) {
      dispatch(
        requestAPI(REPORT_DETAIL_DATA_KEY, {
          url_format: { _id_: reportDetailData.reportPk },
        }),
      );
    }
  }, [dataSourceRefresh, reportDetailData]);

  useEffect(() => {
    if (reportDetailData?.state && viewState?.activeSectionID) {
      dispatch(
        requestAPIHoldingData(
          SECTION_DETAIL_DATA_KEY,
          viewState.activeSectionID,
          {
            url_format: { _id_: viewState.activeSectionID },
          },
        ),
      );
    }
  }, [reportDetailData?.state]);

  function getReportWordcountConfig(additionalParams = {}) {
    const params = {
      url_format: { _id_: reportDetailData.pk },
      data: {
        section_id: viewState.activeSectionID,
      },
      ...additionalParams,
    };

    dispatch(
      requestAPIHoldingData(WORDCOUNT_CONFIG, reportDetailData.pk, params),
    );
  }

  function buildPropsForTotalBeneficiaries() {
    return {
      data:
        sectionDetailData?.datasets?.total_actual_beneficiaries ||
        sectionDetailData?.datasets?.total_adjusted_beneficiaries_age_group,
      period: reportDetailData.period,
    };
  }

  function getComponentForDataSource(dataSource: IDataSource) {
    const { id, title } = dataSource;
    const data: IData = { id, title };
    let PresentationComponent: any;

    const dataSourceSlug: string | string[] = DATA_KEY_ON_DATA_SET[id];

    let dataFromBacked;

    if (Array.isArray(dataSourceSlug)) {
      data.lastRefreshInfo = [];
      dataFromBacked = {};

      for (const slug of dataSourceSlug) {
        dataFromBacked[slug] = sectionDetailData.datasets[slug];
        data.lastRefreshInfo.push(
          sectionDetailData.refresh_date
            ? sectionDetailData.refresh_date?.[slug]
            : null,
        );
      }
    } else {
      dataFromBacked = sectionDetailData.datasets[dataSourceSlug];
      data.lastRefreshInfo = [
        sectionDetailData.refresh_date
          ? sectionDetailData.refresh_date[dataSourceSlug]
          : null,
      ];
    }

    data.lastRefreshInfo = data.lastRefreshInfo.filter(x => !!x);

    let componentsData = [],
      widgetParameters;

    switch (id) {
      case TOTAL_BENEFICIARIES_ID:
        PresentationComponent = DATA_SOURCES_WIDGETS_MAP[id];
        data.component = (
          <PresentationComponent {...buildPropsForTotalBeneficiaries()} />
        );
        break;
      case SDG_CONTRIBUTION_ID:
        data.noModuleTitle = widgetParameters?.noModuleTitle;
        data.containerClassName = widgetParameters?.containerClassName || '';

        data.component = <WFPContributionToSDGs />;
        break;
      default:
        PresentationComponent = DATA_SOURCES_WIDGETS_MAP[id];

        if (!PresentationComponent) {
          data.component = null;
          console.error(
            `No component found for data source id: ${id} (title: ${title})`,
          );
          break;
        }

        componentsData = dataFromBacked || [];
        widgetParameters = getDataSourceWidgetsParametersMap?.[id];
        data.noModuleTitle = widgetParameters?.noModuleTitle;
        data.containerClassName = widgetParameters?.containerClassName || '';
        data.component = (
          <PresentationComponent
            data={componentsData}
            reportState={reportDetailData.state}
            report={reportDetailData}
            currentPeriod={reportDetailData.period}
            {...(widgetParameters?.noModuleTitle ? { title } : {})}
            {...getDataSourceWidgetsParametersMap[id]}
          />
        );
    }
    return data;
  }

  function customOverviewSubtitleAndNarratives() {
    const narratives = sectionDetailData?.narratives;
    const keyMessages = narratives?.find(
      ({ title }) => title === 'Key messages',
    );
    const main = narratives?.find(({ title }) => title === 'Main narrative');

    return (
      <>
        {keyMessages && (
          <Narrative
            narrative={keyMessages}
            isEditEnable={sectionDetailData.permissions.can_edit_narrative}
            isForcePostEnabled={
              sectionDetailData.permissions.can_force_post_narrative
            }
          />
        )}
        {sectionDetailData?.has_subtitle && (
          <Subtitle
            sectionId={viewState.activeSectionID}
            initialContent={sectionDetailData?.custom_subtitle}
            isEditable={
              sectionDetailData.is_content_editable &&
              reportDetailData.state !== 'Published'
            }
          />
        )}
        {main && (
          <Narrative
            narrative={main}
            isEditEnable={sectionDetailData.permissions.can_edit_narrative}
            isForcePostEnabled={
              sectionDetailData.permissions.can_force_post_narrative
            }
          />
        )}
      </>
    );
  }

  const sortedNarratives = orderBy(sectionDetailData?.narratives, 'order');
  const isOverviewPage = sectionDetailData?.current_title === 'Overview';

  return (
    <>
      <Helmet>
        <title>
          {getReportPageTitle(
            reportDetailData?.country,
            reportDetailData?.period,
          )}
        </title>
      </Helmet>
      <main
        className="section-detail new-crf-template"
        id="section-wrapper-main"
      >
        <SectionControls
          error={sectionDetailError}
          dataSourceRefresh={dataSourceRefresh}
          handleRefreshDataSources={handleRefreshDataSources}
        />
        {(!viewState || !reportDetailData || !sectionDetailData) && (
          <div className="section-loader-wrapper">
            <Loading className="section-loader" withOverlay={false} active />
          </div>
        )}
        {sectionDetailData?.has_infographics && (
          <Infographics
            isEdit={isEditInfographics}
            setEdit={(val: boolean) => setIsEditInfographics(val)}
            data={sectionDetailData.infographics_data?.widget_items}
            editorData={sectionDetailData.infographics_editor}
            currentUserUsername={currentUserUsername}
            canEditInfographics={
              reportDetailData.permissions?.infographics_permissions
                ?.edit_infographics
            }
            canUnlockInfographics={
              reportDetailData.permissions?.infographics_permissions
                ?.unlock_infographics
            }
            alignment={sectionDetailData.infographics_data?.alignment}
            renderTutorial={sectionDetailData.tutorialRender}
            saveAction={objToSave => {
              const payload = {
                method: 'post',
                data: {
                  ...objToSave,
                  section_id: viewState.activeSectionID,
                  widget_pk: sectionDetailData.infographics_data?.widget_items,
                },
              };
              dispatch(UpdateInfographicsAction(payload));
            }}
            lockAction={() => {
              const payload = {
                method: 'post',
                data: {
                  section_id: viewState.activeSectionID,
                },
              };
              dispatch(LockInfographicsAction(payload));
            }}
            unlockAction={() => {
              const payload = {
                method: 'post',
                data: {
                  section_id: viewState.activeSectionID,
                },
              };
              dispatch(UnlockInfographicsAction(payload));
            }}
            iconsList={iconsDetailsData}
            reportState={reportDetailData.state}
            domainLink={process.env.REACT_APP_DOMAIN}
            sendGAEvent={sendGAEvent}
          />
        )}
        {!!reportDetailData &&
          !!sectionDetailData?.image_attachment_type &&
          !isStrategicOutcome && <ImageUploader />}

        {!!reportDetailData && isOverviewPage && (
          <ContactInfo
            reportPk={reportDetailData.pk}
            reportIsPublished={reportDetailData.state === 'Published'}
          />
        )}

        {!!reportDetailData && !!sectionDetailData && (
          <Title
            initialContent={sectionDetailData?.current_title}
            isEditable={
              sectionDetailData?.can_change_title &&
              reportDetailData.state !== 'Published'
            }
            sectionId={viewState.activeSectionID}
            headingWidgets={headingWidgets}
          />
        )}

        {!!wordcountConfig && showWordcount && !wordcountConfig?.isFetching && (
          <Module className="section-heading">
            <ModuleBody>
              <WordLimitPanel
                data={wordcountConfig}
                dispatchWordcountConfig={getReportWordcountConfig}
                sectionID={viewState.activeSectionID}
                sectionTitle={sectionDetailData?.current_title}
                isEditable={
                  reportDetailData.state !== 'Published' &&
                  reportDetailData?.permissions
                    ?.has_permission_to_edit_word_count
                }
              />
            </ModuleBody>
          </Module>
        )}

        {isOverviewPage && customOverviewSubtitleAndNarratives()}

        {!isOverviewPage && sectionDetailData?.has_subtitle && (
          <Subtitle
            sectionId={viewState.activeSectionID}
            initialContent={sectionDetailData?.custom_subtitle}
            isEditable={
              sectionDetailData.is_content_editable &&
              reportDetailData.state !== 'Published'
            }
          />
        )}
        {sectionDetailData?.data_holder_detailed_title && (
          <Subtitle
            sectionId={viewState.activeSectionID}
            initialContent={sectionDetailData.data_holder_detailed_title}
            isEditable={false}
            isOptional={false}
            showCharactersLimit={false}
          />
        )}
        {!isOverviewPage &&
          sortedNarratives.map(narrative => (
            <Narrative
              key={narrative.id}
              narrative={narrative}
              isEditEnable={sectionDetailData.permissions.can_edit_narrative}
              isForcePostEnabled={
                sectionDetailData.permissions.can_force_post_narrative
              }
            />
          ))}

        {remainingWidgets.map(item => {
          const isFinancialOverview = item?.id === FINANCIAL_OVERVIEW_ID;

          const displayCertifiedSubtitle =
            parseInt(item?.component?.props?.report?.period) >= 2023 &&
            isFinancialOverview;
          const title = isFinancialOverview
            ? `${item?.title} (in USD)`
            : item?.title;

          return (
            <Module
              key={item?.id}
              data-test-id={item?.title}
              className={`data-source restricted-chart-width ${
                item?.noModuleTitle ? 'special-module-title' : ''
              } ${item?.containerClassName}`}
            >
              {!item?.noModuleTitle && (
                <ModuleHeader
                  className={
                    displayCertifiedSubtitle ? 'with-certified-subtitle' : ''
                  }
                >
                  {title}
                  {displayCertifiedSubtitle && (
                    <div className="certified-subtitle">
                      {item?.component.props.data.certified === 'True'
                        ? 'Certified'
                        : 'Uncertified'}
                      {' version'}
                    </div>
                  )}
                </ModuleHeader>
              )}
              <ModuleBody>{item?.component}</ModuleBody>
              {item?.lastRefreshInfo && item?.id !== SDG_CONTRIBUTION_ID && (
                <LatestRefreshInfo refreshInfo={item.lastRefreshInfo} />
              )}
            </Module>
          );
        })}
        <DataNotes />
        <BottomLinks
          report={reportDetailData}
          currentTitle={sectionDetailData?.current_title}
        />
        <hr className="line-break" />
      </main>
    </>
  );
};

export default WorkspaceACR2023;
